/**
 * TextInput — единый стиль для email/text inputs во всех auth-формах.
 * Та же геометрия что у PasswordInput (px-3.5 py-3) для visual consistency.
 *
 * 7 states (через `state` prop): default / hover / focus / error / focus+error / disabled / success.
 * Slots: leftSlot / rightSlot (icon-i, validation-icon).
 */
import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'

type State = 'default' | 'error' | 'success'

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string
  onChange: (v: string) => void
  /** @deprecated use `state="error"` */
  hasError?: boolean
  state?: State
  leftSlot?: ReactNode
  rightSlot?: ReactNode
}

const baseCls =
  'w-full px-3.5 py-3 rounded-lg border text-sm bg-input-bg-default text-input-text-default placeholder:text-text-muted placeholder:opacity-60 ' +
  'transition-[border-color,box-shadow,background-color] duration-150 focus:outline-none ' +
  'disabled:bg-surface-subtle disabled:text-text-disabled disabled:cursor-not-allowed disabled:opacity-60'

// Ring-color использует Tailwind alpha modifier на токенах с RGB-triple form
// (--border-focus-rgb, --status-danger-text-rgb, --status-success-text-rgb).
// Theme-aware: меняется автоматически между light и dark.
const stateMap: Record<State, string> = {
  default:
    'border-input-border-default hover:border-border-strong focus:border-border-focus focus:ring-2 focus:ring-border-focus/35',
  error:
    'border-status-danger-border bg-status-danger-bg hover:border-status-danger-text ' +
    'focus:border-status-danger-text focus:ring-[3px] focus:ring-status-danger-text/45',
  success:
    'border-status-success-border hover:border-status-success-text ' +
    'focus:border-status-success-text focus:ring-2 focus:ring-status-success-text/35',
}

export const TextInput = forwardRef<HTMLInputElement, Props>(function TextInput(
  { value, onChange, hasError, state, leftSlot, rightSlot, className, ...rest },
  ref
) {
  const resolved: State = hasError ? 'error' : state ?? 'default'
  const padCls = `${leftSlot ? 'pl-10' : ''} ${rightSlot ? 'pr-11' : ''}`
  const cls = `${baseCls} ${stateMap[resolved]} ${padCls} ${className ?? ''}`
  if (leftSlot || rightSlot) {
    return (
      <div className="relative flex items-center">
        {leftSlot && <span className="absolute left-3 inline-flex">{leftSlot}</span>}
        <input
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={resolved === 'error' || undefined}
          className={cls}
          {...rest}
        />
        {rightSlot && <span className="absolute right-3 inline-flex">{rightSlot}</span>}
      </div>
    )
  }
  return (
    <input
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-invalid={resolved === 'error' || undefined}
      className={cls}
      {...rest}
    />
  )
})
